import { Component, JSXElement, Show } from 'solid-js';
import { Localized } from '~/i18n';
import {
  ArrowLeftRightIcon,
  BotIcon,
  BoxesIcon,
  Building2Icon,
  ChartNoAxesColumnIcon,
  FileKey2Icon,
  GemIcon,
  GlobeIcon,
  KeyRoundIcon,
  LayoutGridIcon,
  LockIcon,
  MailIcon,
  MouseIcon,
  PaintbrushIcon,
  RocketIcon,
  ServerIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  SparklesIcon,
  UserCogIcon,
  UserIcon,
  UserRoundIcon,
  UsersIcon,
  WebhookIcon,
  WorkflowIcon,
  ZapIcon,
  LayoutGrid
} from 'lucide-solid';
import {
  AnyAppPath,
  ConnectionDetailsTabTypes,
  EnvAppPaths,
  EnvironmentPagePath,
  getSlug,
  MyAccountPagePath,
  replacePathParams,
  WorkspacePagePath
} from '~/components/AppRouter/utils.ts';
import { EnvironmentData, EnvironmentTypes } from '~/api';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import {
  isFeatureEnabled,
  isFeatureEnabledForOrg
} from '~/utils/features/featureFlag.tsx';
import { SINGLE_SIGNON_TAB_TYPES } from '~/pages/Authentication/SingleSignOn/utils.ts';
import { DIRECTORY_SYNC_TAB_TYPES } from '~/pages/Authentication/DirectorySync/utils.ts';
import { CustomizationTabTypes } from '~/pages/Customizations/utils';
import McpIcon from '~/assets/mcp-icon.svg?component-solid';
import { usePortalCustomizationContext } from '~/portal-customization/PortalCustomizations.tsx';
import { BillingTabTypes, isBillingEnabled } from '~/pages/Billing/utils.ts';
import {
  allOf,
  anyOf,
  EnvAppPermissions,
  isAuthorized
} from '~/components/Authorization/utils.ts';
import { useOrganizationContext } from '~/components/AppData/OrganizationContext.tsx';
import {
  ScalekitWatermark,
  useScalekitWatermarkVisibility
} from '~/components/ScalekitWatermark';

export type LeftNavLinkConfig = {
  iconComponent?: Component;
  labelComponent: Component;
  href: string;
  hidden?: boolean;
  dataTestId?: string;
};

export type LeftNavGroupConfig = {
  labelComponent?: Component;
  items: Array<LeftNavLinkConfig>;
};

export enum LeftNavType {
  WORKSPACE = 'WORKSPACE',
  ENVIRONMENT = 'ENVIRONMENT'
}

export const getLabelComponent = ({
  translationKey,
  upperCase = false,
  iconComponent,
  className = '',
  labelTag
}: {
  translationKey?: string;
  upperCase?: boolean;
  iconComponent?: Component<{
    strokeWidth?: number;
    class?: string;
  }>;
  className?: string;
  labelTag?: JSXElement;
}): Component => {
  return (): JSXElement => {
    const IconComponent: Component<{
      width?: number;
      height?: number;
      strokeWidth?: number;
      class?: string;
    }> = iconComponent!;
    return (
      <div class={`flex items-center`}>
        {IconComponent && (
          <IconComponent
            width={20}
            height={20}
            strokeWidth={1}
            class={`me-2`}
          />
        )}
        <span class={className}>
          <Show
            when={translationKey}
            fallback={labelTag ? labelTag : <></>}
          >
            <Localized
              upperCase={upperCase}
              translationKey={translationKey!}
            />
          </Show>
        </span>
      </div>
    );
  };
};

const getMenuItemLabelTranslationPath = (translationStr: string) =>
  `page_titles.${translationStr}`;

const getConfigurationGroup = (
  selectedEnvironment: EnvironmentData
): Array<LeftNavGroupConfig> => {
  const selectedEnvironmentId = selectedEnvironment?.id;
  const configurationGroup: LeftNavGroupConfig = {
    labelComponent: getLabelComponent({
      translationKey: 'left_nav_group_labels.configure',
      upperCase: false,
      className: 'font-bold text-sm text-fg-muted'
    }),
    items: []
  };

  if (isFeatureEnabled(FeatureFlagKeys.AUTHENTICATION_METHODS)) {
    configurationGroup.items.push({
      iconComponent: LockIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`authentication`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION,
        true
      ),
      hidden: !isFeatureEnabled(FeatureFlagKeys.AUTHENTICATION_METHODS),
      dataTestId: 'authentication-left-nav'
    });
  } else {
    configurationGroup.items.push({
      iconComponent: LayoutGridIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`social_login`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_SOCIAL_CONNECTIONS,
        true
      ),
      hidden: isFeatureEnabled(FeatureFlagKeys.AUTHENTICATION_METHODS)
    });
  }

  if (isFeatureEnabled(FeatureFlagKeys.USER_MANAGEMENT_SETTINGS)) {
    configurationGroup.items.push({
      iconComponent: UserCogIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`user_attributes`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_USER_ATTRIBUTES,
        true
      ),
      dataTestId: 'user-attributes-left-nav'
    });
  } else {
    configurationGroup.items.push({
      iconComponent: KeyRoundIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`sso`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        replacePathParams(EnvironmentPagePath.ENVIRONMENT_SINGLE_SIGN_ON, {
          tabType: SINGLE_SIGNON_TAB_TYPES.ATTRIBUTES
        }) as EnvironmentPagePath,
        true
      )
    });
  }

  if (isFeatureEnabled(FeatureFlagKeys.FSA_AUTHORIZATION)) {
    configurationGroup.items.push({
      iconComponent: ShieldCheckIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`authorization`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_AUTHORIZATION,
        true
      ),
      dataTestId: 'authorization-left-nav'
    });
  } else {
    configurationGroup.items.push({
      iconComponent: ArrowLeftRightIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`directory_sync`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        replacePathParams(EnvironmentPagePath.ENVIRONMENT_DIRECTORY_SYNC, {
          tabType: DIRECTORY_SYNC_TAB_TYPES.ROLES
        }) as EnvironmentPagePath,
        true
      )
    });
  }

  if (!isFeatureEnabled(FeatureFlagKeys.FSA_SETTINGS)) {
    const customizationItem: LeftNavLinkConfig = {
      iconComponent: MouseIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`customization`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        replacePathParams(EnvironmentPagePath.ENVIRONMENT_CUSTOMIZATIONS, {
          tabType: CustomizationTabTypes.BRANDING_AND_THEMING
        }) as EnvironmentPagePath,
        true
      )
    };
    configurationGroup.items.push(customizationItem);
  }

  if (isFeatureEnabled(FeatureFlagKeys.MCP_SERVERS)) {
    configurationGroup.items.push({
      iconComponent: McpIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`mcp_servers`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_MCP_SERVERS_LIST,
        true
      ),
      dataTestId: 'mcp-servers-left-nav'
    });
  }

  if (isFeatureEnabled(FeatureFlagKeys.SCALEKIT_AGENT_AUTH)) {
    configurationGroup.items.push({
      iconComponent: BotIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`agent_auth`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.SCALEKIT_AGENT_AUTH,
        true
      ),
      dataTestId: 'agent-auth-left-nav'
    });
  }

  return (
    configurationGroup.items.length ? [configurationGroup] : []
  ) as LeftNavGroupConfig[];
};

const getDevelopersGroup = (
  selectedEnvironment: EnvironmentData
): Array<LeftNavGroupConfig> => {
  const selectedEnvironmentId = selectedEnvironment?.id;
  const developersGroup: LeftNavGroupConfig = {
    labelComponent: getLabelComponent({
      translationKey: 'left_nav_group_labels.developer_settings',
      upperCase: false,
      className: 'font-bold text-sm text-fg-muted'
    }),
    items: []
  };

  developersGroup.items.push({
    iconComponent: LayoutGrid,
    labelComponent: getLabelComponent({
      translationKey: getMenuItemLabelTranslationPath(`multi_applications`),
      className: 'text-fg-muted'
    }),
    href: getSlug.environmentById(
      selectedEnvironmentId,
      EnvironmentPagePath.ENVIRONMENT_MULTI_APPLICATION,
      true
    ),
    dataTestId: 'multi-applications-left-nav'
  });

  if (isFeatureEnabled(FeatureFlagKeys.WEBHOOKS)) {
    developersGroup.items.push({
      iconComponent: WebhookIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`webhooks`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_WEBHOOKS,
        true
      ),
      dataTestId: 'webhooks-left-nav'
    });
  }

  if (isFeatureEnabled(FeatureFlagKeys.INTERCEPTORS)) {
    developersGroup.items.push({
      iconComponent: WorkflowIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`interceptors`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_INTERCEPTORS,
        true
      ),
      dataTestId: 'interceptors-left-nav'
    });
  }

  if (isFeatureEnabled(FeatureFlagKeys.AUTHENTICATION_LOGS)) {
    developersGroup.items.push({
      iconComponent: ChartNoAxesColumnIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`auth_logs`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION_LOGS,
        true
      ),
      dataTestId: 'auth-logs-left-nav'
    });
  }

  if (isFeatureEnabled(FeatureFlagKeys.FSA_SETTINGS)) {
    developersGroup.items.push({
      iconComponent: SlidersHorizontalIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`settings`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_SETTINGS,
        true
      ),
      dataTestId: 'settings-left-nav'
    });
  } else {
    developersGroup.items.push({
      iconComponent: FileKey2Icon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`api_credentials`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_API_CREDENTIALS,
        true
      )
    });
  }

  return (
    developersGroup.items.length ? [developersGroup] : []
  ) as LeftNavGroupConfig[];
};

const getConnectGroup = (
  selectedEnvironment: EnvironmentData
): Array<LeftNavGroupConfig> => {
  if (!isFeatureEnabled(FeatureFlagKeys.SCALEKIT_CONNECT)) {
    return [];
  }
  const selectedEnvironmentId = selectedEnvironment?.id;
  return [
    {
      labelComponent: getLabelComponent({
        translationKey: 'left_nav_group_labels.agent_actions',
        upperCase: false,
        className: 'font-bold text-sm text-fg-muted'
      }),
      items: [
        {
          iconComponent: BoxesIcon,
          labelComponent: getLabelComponent({
            translationKey:
              getMenuItemLabelTranslationPath(`connect_integrations`),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            EnvironmentPagePath.SCALEKIT_CONNECT_INTEGRATIONS,
            true
          ),
          dataTestId: 'connect-integrations-left-nav'
        },
        {
          iconComponent: ServerIcon,
          labelComponent: getLabelComponent({
            translationKey:
              getMenuItemLabelTranslationPath(`connected_accounts`),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            EnvironmentPagePath.SCALEKIT_CONNECTED_ACCOUNTS,
            true
          ),
          dataTestId: 'connected-accounts-left-nav'
        },
        {
          iconComponent: SparklesIcon,
          labelComponent: getLabelComponent({
            translationKey: getMenuItemLabelTranslationPath(`playground`),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            EnvironmentPagePath.SCALEKIT_CONNECT_TOOL_EXECUTION_PLAYGROUND,
            true
          ),
          dataTestId: 'playground-left-nav'
        }
      ]
    }
  ];
};

const getCustomizationGroup = (
  selectedEnvironment: EnvironmentData
): Array<LeftNavGroupConfig> => {
  const selectedEnvironmentId = selectedEnvironment?.id;

  const customizationGroup: LeftNavGroupConfig = {
    labelComponent: getLabelComponent({
      translationKey: 'left_nav_group_labels.customize',
      upperCase: false,
      className: 'font-bold text-sm text-fg-muted'
    }),
    items: []
  };

  if (isFeatureEnabled(FeatureFlagKeys.FSA_CUSTOMIZATION_ENABLED)) {
    const customizationItem: LeftNavLinkConfig = {
      iconComponent: PaintbrushIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(
          isFeatureEnabled(FeatureFlagKeys.FSA_SETTINGS)
            ? `customization`
            : `login_customization`
        ),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_BRANDING,
        true
      ),
      dataTestId: 'customization-left-nav'
    };
    customizationGroup.items.push(customizationItem);
  }

  if (isFeatureEnabled(FeatureFlagKeys.EMAIL_CONFIGURATION)) {
    const emailConfigItem: LeftNavLinkConfig = {
      iconComponent: MailIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`email_configuration`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_EMAILS,
        true
      ),
      dataTestId: 'email-configuration-left-nav'
    };
    customizationGroup.items.push(emailConfigItem);
  }

  if (isFeatureEnabled(FeatureFlagKeys.FSA_SETTINGS)) {
    customizationGroup.items.push({
      iconComponent: GlobeIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`custom_domain_title`),
        className: 'text-fg-muted'
      }),
      href: getSlug.environmentById(
        selectedEnvironmentId,
        EnvironmentPagePath.ENVIRONMENT_CUSTOM_DOMAIN,
        true
      ),
      dataTestId: 'custom-domain-left-nav'
    });
  }

  return (
    customizationGroup.items.length ? [customizationGroup] : []
  ) as LeftNavGroupConfig[];
};

const getAppEnvironmentsNavConfig = (
  selectedEnvironment: EnvironmentData
): Array<LeftNavGroupConfig> => {
  const selectedEnvironmentId = selectedEnvironment?.id;

  return [
    {
      items: [
        {
          iconComponent: ZapIcon,
          labelComponent: getLabelComponent({
            translationKey: getMenuItemLabelTranslationPath(`quick_start`),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            EnvironmentPagePath.ENVIRONMENT_QUICK_START,
            true
          ),
          hidden:
            !isFeatureEnabled(FeatureFlagKeys.QUICK_START) ||
            selectedEnvironment.type !== EnvironmentTypes.DEVELOPMENT,
          dataTestId: 'quickstart-left-nav'
        },
        {
          iconComponent: RocketIcon,
          labelComponent: getLabelComponent({
            translationKey: getMenuItemLabelTranslationPath(`getting_started`),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            EnvironmentPagePath.ENVIRONMENT_GETTING_STARTED,
            true
          ),
          hidden: import.meta.env.VITE_GETTING_STARTED_ENABLED !== 'true'
        },
        {
          iconComponent: Building2Icon,
          labelComponent: getLabelComponent({
            translationKey: getMenuItemLabelTranslationPath(`organization`),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            EnvironmentPagePath.ENVIRONMENT_ORGANIZATIONS,
            true
          ),
          dataTestId: 'organizations-left-nav'
        },
        {
          iconComponent: UsersIcon,
          labelComponent: getLabelComponent({
            translationKey: getMenuItemLabelTranslationPath(`users`),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            EnvironmentPagePath.ENVIRONMENT_USERS,
            true
          ),
          hidden: !isFeatureEnabled(FeatureFlagKeys.USERS),
          dataTestId: 'users-left-nav'
        }
      ]
    },
    ...getConfigurationGroup(selectedEnvironment),
    ...(!isFeatureEnabled(FeatureFlagKeys.SCALEKIT_AGENT_AUTH)
      ? getConnectGroup(selectedEnvironment)
      : []),
    ...getCustomizationGroup(selectedEnvironment),
    ...getDevelopersGroup(selectedEnvironment)
  ];
};

const getAppWorkspaceNavConfig = (): Array<LeftNavGroupConfig> => {
  return [
    {
      labelComponent: getLabelComponent({
        translationKey: 'left_nav_group_labels.workspace',
        upperCase: false,
        iconComponent: GemIcon,
        className: 'text-sm'
      }),
      items: (() => {
        const items: LeftNavLinkConfig[] = [
          {
            labelComponent: getLabelComponent({
              translationKey:
                getMenuItemLabelTranslationPath(`workspace_general`)
            }),
            href: getSlug.workspace(WorkspacePagePath.WORKSPACE_GENERAL, true),
            dataTestId: 'workspace-general-left-nav'
          },
          {
            labelComponent: getLabelComponent({
              translationKey:
                getMenuItemLabelTranslationPath(`manage_environments`)
            }),
            href: getSlug.workspace(
              WorkspacePagePath.WORKSPACE_MANAGE_ENVIRONMENTS,
              true
            ),
            dataTestId: 'workspace-environments-left-nav'
          },
          {
            labelComponent: getLabelComponent({
              translationKey: getMenuItemLabelTranslationPath(`members`)
            }),
            href: getSlug.workspace(WorkspacePagePath.WORKSPACE_MEMBERS, true),
            dataTestId: 'workspace-team-members-left-nav'
          }
        ];
        if (isBillingEnabled()) {
          items.push({
            labelComponent: getLabelComponent({
              translationKey: getMenuItemLabelTranslationPath(`billing`)
            }),
            href: replacePathParams(
              getSlug.workspace(
                WorkspacePagePath.WORKSPACE_BILLING,
                true
              ) as AnyAppPath,
              { tabType: BillingTabTypes.DETAILS }
            ),
            dataTestId: 'workspace-billing-left-nav'
          });
        } else if (isFeatureEnabled(FeatureFlagKeys.BILLING)) {
          items.push({
            labelComponent: getLabelComponent({
              translationKey: getMenuItemLabelTranslationPath(`billing`)
            }),
            href: getSlug.workspace(
              WorkspacePagePath.WORKSPACE_BILLING_V1,
              true
            ),
            dataTestId: 'workspace-billing-left-nav'
          });
        }
        return items;
      })()
    },
    {
      labelComponent: getLabelComponent({
        translationKey: 'left_nav_group_labels.my_account',

        upperCase: false,
        iconComponent: UserRoundIcon,
        className: 'text-sm'
      }),
      items: [
        {
          labelComponent: getLabelComponent({
            translationKey: getMenuItemLabelTranslationPath(`my_profile`)
          }),
          href: getSlug.myAccount(MyAccountPagePath.MYACCOUNT_PROFILE, true),
          dataTestId: 'my-account-profile-left-nav'
        }
      ]
    }
  ];
};

const getEnvAppNavConfig = (): Array<LeftNavGroupConfig> => {
  const portalCustomization = usePortalCustomizationContext();
  const organizationContext = useOrganizationContext();
  const isSsoEnabledForOrg = isFeatureEnabledForOrg(
    organizationContext.organization!,
    FeatureFlagKeys.SINGLE_SIGNON
  );
  const isScimEnabledForOrg = isFeatureEnabledForOrg(
    organizationContext.organization!,
    FeatureFlagKeys.DIRECTORY_SYNC
  );

  const organizationGroup: LeftNavLinkConfig[] = [];
  if (
    isAuthorized(
      allOf(
        anyOf(
          EnvAppPermissions.ORG_SETTINGS_READ,
          EnvAppPermissions.ORG_SETTINGS_MANAGE
        )
      )
    )
  ) {
    organizationGroup.push({
      iconComponent: SettingsIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`env_app_general`),
        className: 'text-fg-muted'
      }),
      href: getSlug.envAppPages(EnvAppPaths.MANAGE_WORKSPACE, true),
      dataTestId: 'general-left-nav'
    });
  }
  if (
    isAuthorized(
      allOf(
        anyOf(
          EnvAppPermissions.ORG_SETTINGS_READ,
          EnvAppPermissions.ORG_SETTINGS_MANAGE
        ),
        anyOf(EnvAppPermissions.ORG_USERS_READ)
      )
    )
  ) {
    organizationGroup.push({
      iconComponent: UsersIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`env_app_users`),
        className: 'text-fg-muted'
      }),
      href: getSlug.envAppPages(EnvAppPaths.USERS, true),
      dataTestId: 'general-left-nav'
    });
  }
  if (
    isSsoEnabledForOrg &&
    isAuthorized(
      allOf(
        anyOf(
          EnvAppPermissions.ORG_SETTINGS_READ,
          EnvAppPermissions.ORG_SETTINGS_MANAGE
        ),
        anyOf(EnvAppPermissions.ORG_SSO_READ, EnvAppPermissions.ORG_SSO_MANAGE)
      )
    )
  ) {
    organizationGroup.push({
      iconComponent: KeyRoundIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`env_app_sso`),
        className: 'text-fg-muted'
      }),
      href: replacePathParams(
        getSlug.envAppPages(EnvAppPaths.LIST_CONNECTIONS, true) as AnyAppPath,
        {
          connectionTabType: ConnectionDetailsTabTypes.CONFIGURATION
        }
      ),
      dataTestId: 'general-left-nav'
    });
  }
  if (
    isScimEnabledForOrg &&
    isAuthorized(
      allOf(
        anyOf(
          EnvAppPermissions.ORG_SETTINGS_READ,
          EnvAppPermissions.ORG_SETTINGS_MANAGE
        ),
        anyOf(
          EnvAppPermissions.ORG_SCIM_READ,
          EnvAppPermissions.ORG_SCIM_MANAGE
        )
      )
    )
  ) {
    organizationGroup.push({
      iconComponent: ArrowLeftRightIcon,
      labelComponent: getLabelComponent({
        translationKey: getMenuItemLabelTranslationPath(`env_app_scim`),
        className: 'text-fg-muted'
      }),
      href: getSlug.envAppPages(EnvAppPaths.WORKSPACE_DIRECTORY_SYNC, true),
      dataTestId: 'general-left-nav'
    });
  }

  return [
    ...(organizationGroup.length
      ? [
          {
            labelComponent: getLabelComponent({
              labelTag: portalCustomization.appConfig?.organizationMetaName,
              upperCase: false,
              className: 'text-sm'
            }),
            items: organizationGroup
          }
        ]
      : []),
    {
      labelComponent: getLabelComponent({
        translationKey: 'left_nav_group_labels.env_app_profile',
        upperCase: false,
        className: 'text-sm'
      }),
      items: [
        {
          iconComponent: UserIcon,
          labelComponent: getLabelComponent({
            translationKey:
              getMenuItemLabelTranslationPath(`env_app_my_account`),
            className: 'text-fg-muted'
          }),
          href: getSlug.envAppPages(EnvAppPaths.MY_ACCOUNT, true),
          dataTestId: 'general-left-nav'
        },
        {
          iconComponent: LockIcon,
          labelComponent: getLabelComponent({
            translationKey:
              getMenuItemLabelTranslationPath(`env_app_my_security`),
            className: 'text-fg-muted'
          }),
          href: getSlug.envAppPages(EnvAppPaths.MY_SECURITY, true),
          dataTestId: 'general-left-nav'
        }
      ]
    }
  ];
};

const getEnvAppNavFooterConfig = (): JSXElement => {
  const { shouldShowWatermark } = useScalekitWatermarkVisibility();

  return (
    <Show when={shouldShowWatermark()}>
      <ScalekitWatermark class="bg-transparent" />
    </Show>
  );
};

export {
  getAppEnvironmentsNavConfig,
  getAppWorkspaceNavConfig,
  getEnvAppNavConfig,
  getEnvAppNavFooterConfig
};
